import React, { useCallback, useEffect, useRef, useState, } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getProductDetails2, } from "../../../ProductDetail/service/productdetails.service";
import { getCustomers } from "../../../Cart/service/Customers.service";
import { getCartCount} from "../../../../Component/header/service/CartCount.service";

const useQRScanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousCustomer = location.state?.customer || null;
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(false);
  const startingRef = useRef(false);
  const scannedRef = useRef(false);
  const restartTimerRef = useRef(null);
  const startScannerRef = useRef(null);

  // =====================================================
  // CUSTOMER STATE
  // =====================================================

  const [selectedCustomer, setSelectedCustomer] = useState(previousCustomer);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);

  // =====================================================
  // QR STATE
  // =====================================================

  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  // =====================================================
  // PRODUCT STATE
  // =====================================================

  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  // =====================================================
  // NORMALIZE PRODUCT
  // =====================================================

  const normalizeProduct = useCallback(
    (response) => {
      const data =
        response?.data ?? response;

      if (Array.isArray(data)) {
        return data[0] || null;
      }

      if (Array.isArray(data?.data)) {
        return data.data[0] || null;
      }

      if (Array.isArray(data?.response)) {
        return data.response[0] || null;
      }

      if (
        data?.data &&
        typeof data.data === "object"
      ) {
        return data.data;
      }

      if (
        data &&
        typeof data === "object"
      ) {
        return data;
      }

      return null;
    },
    []
  );

  // =====================================================
  // EXTRACT SLUG
  // =====================================================

  const extractSlug = useCallback(
    (value) => {
      const text =
        String(value || "").trim();

      if (!text) {
        return "";
      }

      try {
        const url = new URL(text);

        const parts =
          url.pathname
            .split("/")
            .filter(Boolean);

        return decodeURIComponent(
          parts[parts.length - 1] || ""
        );
      } catch {
        try {
          return decodeURIComponent(text);
        } catch {
          return text;
        }
      }
    },
    []
  );

  // =====================================================
  // CLOSE CUSTOMER MODAL
  // =====================================================

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setCustomerSearch("");
    setCustomers([]);
    setShowCustomerDropdown(false);
    setActiveCustomerIndex(-1);

    scannedRef.current = false;
  };

  useEffect(() => {
    if (
      !showCustomerModal &&
      !selectedCustomer
    ) {
      return;
    }

    if (showCustomerModal) {
      return;
    }

    const timer = setTimeout(() => {
      if (
        mountedRef.current &&
        selectedCustomer
      ) {
        startScannerRef.current?.();
      }
    }, 300);

    return () =>
      clearTimeout(timer);
  }, [
    showCustomerModal,
    selectedCustomer,
  ]);

  // =====================================================
  // STOP SCANNER
  // =====================================================

  const stopScanner = useCallback(() => {
    try {
      controlsRef.current?.stop?.();
    } catch (e) {
      console.log(
        "ZXing stop error:",
        e
      );
    }

    controlsRef.current = null;

    try {
      readerRef.current?.reset?.();
    } catch (e) {
      console.log(
        "ZXing reset error:",
        e
      );
    }

    readerRef.current = null;

    const video =
      videoRef.current;

    const stream =
      streamRef.current ||
      video?.srcObject;

    if (stream) {
      stream
        .getTracks()
        .forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            console.log(e);
          }
        });
    }

    streamRef.current = null;

    if (video) {
      try {
        video.pause();
      } catch (e) {
        console.log(e);
      }

      video.srcObject = null;
    }

    if (mountedRef.current) {
      setScanning(false);
      setStarting(false);
    }
  }, []);

  // =====================================================
  // CHECK CART
  // =====================================================

  const checkCartCustomer = useCallback(
    async () => {
      try {
        const response =
          await getCartCount(
            "fresh",
            1,
            null
          );

        console.log(
          "CART RESPONSE:",
          response
        );

        const cartData =
          response?.data?.data ||
          response?.data ||
          response ||
          {};

        const customerId =
          cartData?.customer_id ??
          response?.customer_id ??
          response?.data?.customer_id ??
          null;

        console.log(
          "CART CUSTOMER ID:",
          customerId
        );

        if (customerId) {
          const list =
            await getCustomers("");

          const customerList =
            Array.isArray(list)
              ? list
              : [];

          const customer =
            customerList.find(
              (item) =>
                Number(item.id) ===
                Number(customerId)
            );

          if (customer) {
            setSelectedCustomer(
              customer
            );

            setShowCustomerModal(
              false
            );

            scannedRef.current =
              false;

            setTimeout(() => {
              if (
                mountedRef.current
              ) {
                startScannerRef.current?.();
              }
            }, 300);

            return;
          }
        }

        setSelectedCustomer(null);

        stopScanner();

        setShowCustomerModal(true);
      } catch (error) {
        console.error(
          "Check Cart Customer Error:",
          error
        );

        setSelectedCustomer(null);

        stopScanner();

        setShowCustomerModal(true);
      }
    },
    [stopScanner]
  );

  // =====================================================
  // CUSTOMER SEARCH
  // =====================================================

  useEffect(() => {
    if (!showCustomerModal) {
      return;
    }

    const value =
      customerSearch.trim();

    if (!value) {
      setCustomers([]);
      setShowCustomerDropdown(false);
      setActiveCustomerIndex(-1);
      return;
    }

    setActiveCustomerIndex(-1);

    const timer = setTimeout(
      async () => {
        try {
          setCustomerLoading(true);

          const list =
            await getCustomers(value);

          setCustomers(
            Array.isArray(list)
              ? list
              : []
          );

          setShowCustomerDropdown(
            true
          );
        } catch (err) {
          console.error(
            "Customer Search Error:",
            err
          );

          setCustomers([]);
          setShowCustomerDropdown(
            true
          );
        } finally {
          setCustomerLoading(false);
        }
      },
      300
    );

    return () =>
      clearTimeout(timer);
  }, [
    customerSearch,
    showCustomerModal,
  ]);

  // =====================================================
  // SELECT CUSTOMER
  // =====================================================

  const handleCustomerSelect =
    useCallback(
      (customer) => {
        if (!customer?.id) {
          return;
        }

        setSelectedCustomer(
          customer
        );

        setCustomerSearch("");
        setCustomers([]);
        setShowCustomerDropdown(
          false
        );
        setActiveCustomerIndex(-1);

        setShowCustomerModal(false);

        scannedRef.current = false;

        if (
          restartTimerRef.current
        ) {
          clearTimeout(
            restartTimerRef.current
          );
        }

        restartTimerRef.current =
          setTimeout(() => {
            if (
              mountedRef.current
            ) {
              startScannerRef.current?.();
            }
          }, 300);
      },
      []
    );

  // =====================================================
  // CUSTOMER KEYBOARD
  // =====================================================

  const handleCustomerKeyDown = (
    event
  ) => {
    if (
      !showCustomerDropdown ||
      customers.length === 0
    ) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();

      setActiveCustomerIndex(
        (prev) => {
          if (prev < 0) {
            return 0;
          }

          if (
            prev >=
            customers.length - 1
          ) {
            return (
              customers.length - 1
            );
          }

          return prev + 1;
        }
      );

      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      setActiveCustomerIndex(
        (prev) => {
          if (prev <= 0) {
            return 0;
          }

          return prev - 1;
        }
      );

      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (
        activeCustomerIndex >= 0 &&
        activeCustomerIndex <
          customers.length
      ) {
        handleCustomerSelect(
          customers[
            activeCustomerIndex
          ]
        );
      }
    }
  };

  // =====================================================
  // HIGHLIGHT
  // =====================================================

  const highlightText = (
    text,
    searchValue
  ) => {
    if (!text || !searchValue) {
      return text;
    }

    const words =
      searchValue
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!words.length) {
      return text;
    }

    const escapedWords =
      words.map((word) =>
        word.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        )
      );

    const regex =
      new RegExp(
        `(${escapedWords.join("|")})`,
        "gi"
      );

    return String(text)
      .split(regex)
      .map((part, index) => {
        const isMatch =
          words.some(
            (word) =>
              part.toLowerCase() ===
              word.toLowerCase()
          );

        return isMatch ? (
          <mark
            key={index}
            style={{
              background:
                "transparent",
              fontWeight: 700,
              color: "#000",
              padding: "0",
            }}
          >
            {part}
          </mark>
        ) : (
          <React.Fragment
            key={index}
          >
            {part}
          </React.Fragment>
        );
      });
  };

  // =====================================================
  // FETCH PRODUCT
  // =====================================================

  const fetchProduct =
    useCallback(
      async (qrValue) => {
        const slug =
          extractSlug(qrValue);

        if (!slug) {
          toast.error(
            "Invalid QR code"
          );

          scannedRef.current = false;

          return;
        }

        setProductLoading(true);
        setProductError("");
        setProduct(null);
        setCurrentImage(0);
        setShowProductModal(true);

        try {
          const response =
            await getProductDetails2(
              slug
            );

          const data =
            normalizeProduct(
              response
            );

          if (!data) {
            throw new Error(
              "Product not found"
            );
          }

          if (
            !mountedRef.current
          ) {
            return;
          }

          setProduct(data);
          setProductError("");
          setCurrentImage(0);
        } catch (err) {
          console.error(
            "QR Product Error:",
            err
          );

          if (
            !mountedRef.current
          ) {
            return;
          }

          const message =
            err?.response?.data
              ?.message ||
            err?.message ||
            "Product not found";

          setProductError(
            message
          );

          toast.error(message);

          setShowProductModal(false);
          setProduct(null);

          scannedRef.current =
            false;

          restartTimerRef.current =
            setTimeout(() => {
              if (
                mountedRef.current
              ) {
                startScannerRef.current?.();
              }
            }, 250);
        } finally {
          if (
            mountedRef.current
          ) {
            setProductLoading(
              false
            );
          }
        }
      },
      [
        extractSlug,
        normalizeProduct,
      ]
    );

  // =====================================================
  // QR FOUND
  // =====================================================

  const onQRCodeFound =
    useCallback(
      async (text) => {
        if (
          !mountedRef.current
        ) {
          return;
        }

        if (
          scannedRef.current
        ) {
          return;
        }

        if (!selectedCustomer) {
          toast.error(
            "Please select customer first"
          );
          return;
        }

        const value =
          String(text || "").trim();

        if (!value) {
          return;
        }

        scannedRef.current = true;

        stopScanner();

        await fetchProduct(value);
      },
      [
        fetchProduct,
        selectedCustomer,
        stopScanner,
      ]
    );

  // =====================================================
  // START SCANNER
  // =====================================================

  const startScanner =
    useCallback(
      async () => {
        if (
          !mountedRef.current
        ) {
          return;
        }

        if (!selectedCustomer) {
          return;
        }

        if (
          startingRef.current
        ) {
          return;
        }

        if (
          controlsRef.current
        ) {
          return;
        }

        const video =
          videoRef.current;

        if (!video) {
          return;
        }

        if (!window.isSecureContext) {
          setError(
            "Camera requires HTTPS."
          );
          return;
        }

        if (
          !navigator.mediaDevices ||
          !navigator.mediaDevices
            .getUserMedia
        ) {
          setError(
            "Camera is not supported in this browser."
          );
          return;
        }

        startingRef.current = true;

        setStarting(true);
        setError("");

        try {
          stopScanner();

          await new Promise(
            (resolve) =>
              setTimeout(
                resolve,
                80
              )
          );

          if (
            !mountedRef.current
          ) {
            return;
          }

          const reader =
            new BrowserQRCodeReader();

          readerRef.current =
            reader;

          const constraints = {
            video: {
              facingMode: {
                ideal:
                  "environment",
              },
              width: {
                ideal: 1280,
                max: 1280,
              },
              height: {
                ideal: 720,
                max: 720,
              },
              frameRate: {
                ideal: 30,
                max: 30,
              },
            },
            audio: false,
          };

          const controls =
            await reader.decodeFromConstraints(
              constraints,
              video,
              (decoded) => {
                if (
                  !mountedRef.current
                ) {
                  return;
                }

                if (
                  scannedRef.current
                ) {
                  return;
                }

                if (!decoded) {
                  return;
                }

                const text =
                  decoded
                    .getText()
                    ?.trim();

                if (!text) {
                  return;
                }

                onQRCodeFound(text);
              }
            );

          if (
            !mountedRef.current
          ) {
            controls?.stop?.();
            return;
          }

          controlsRef.current =
            controls;

          const stream =
            video.srcObject;

          streamRef.current =
            stream;

          const track =
            stream?.getVideoTracks?.()[0];

          if (track) {
            try {
              const capabilities =
                track.getCapabilities?.();

              const advanced = [];

              if (
                capabilities?.focusMode?.includes(
                  "continuous"
                )
              ) {
                advanced.push({
                  focusMode:
                    "continuous",
                });
              }

              if (
                capabilities?.frameRate?.max >=
                30
              ) {
                advanced.push({
                  frameRate: 30,
                });
              }

              if (
                advanced.length
              ) {
                await track.applyConstraints(
                  {
                    advanced,
                  }
                );
              }
            } catch (e) {
              console.log(
                "Camera optimization unavailable",
                e
              );
            }
          }

          video.setAttribute(
            "playsinline",
            "true"
          );

          video.muted = true;

          try {
            await video.play();
          } catch (e) {
            console.log(e);
          }

          if (
            !mountedRef.current
          ) {
            return;
          }

          setStarting(false);
          setScanning(true);
        } catch (err) {
          console.error(
            "Camera error:",
            err
          );

          if (
            !mountedRef.current
          ) {
            return;
          }

          let message =
            "Unable to start camera.";

          if (
            err?.name ===
            "NotAllowedError"
          ) {
            message =
              "Camera permission denied. Please allow camera access.";
          } else if (
            err?.name ===
            "NotFoundError"
          ) {
            message =
              "No camera found.";
          } else if (
            err?.name ===
            "NotReadableError"
          ) {
            message =
              "Camera is already being used by another app.";
          } else if (
            err?.name ===
            "SecurityError"
          ) {
            message =
              "Camera blocked by browser security.";
          } else if (
            err?.name ===
            "OverconstrainedError"
          ) {
            message =
              "Camera settings are not supported.";
          } else if (
            err?.message
          ) {
            message =
              err.message;
          }

          setError(message);

          stopScanner();
        } finally {
          startingRef.current =
            false;

          if (
            mountedRef.current
          ) {
            setStarting(false);
          }
        }
      },
      [
        onQRCodeFound,
        selectedCustomer,
        stopScanner,
      ]
    );

  startScannerRef.current =
    startScanner;

  // =====================================================
  // MOUNT
  // =====================================================

  useEffect(() => {
    mountedRef.current = true;

    if (previousCustomer) {
      setSelectedCustomer(
        previousCustomer
      );

      setShowCustomerModal(false);

      setTimeout(() => {
        if (
          mountedRef.current
        ) {
          startScannerRef.current?.();
        }
      }, 300);
    } else {
      checkCartCustomer();
    }

    return () => {
      mountedRef.current = false;

      if (
        restartTimerRef.current
      ) {
        clearTimeout(
          restartTimerRef.current
        );

        restartTimerRef.current =
          null;
      }

      stopScanner();
    };
  }, [
    previousCustomer,
    checkCartCustomer,
    stopScanner,
  ]);

  // =====================================================
  // IMAGES
  // =====================================================

  const getProductImages =
    useCallback(
      (productData) => {
        if (!productData) {
          return [];
        }

        const images = [];

        if (
          productData.image_path
        ) {
          images.push(
            productData.image_path
          );
        }

        if (
          Array.isArray(
            productData.images
          )
        ) {
          productData.images.forEach(
            (image) => {
              if (
                typeof image ===
                "string"
              ) {
                images.push(image);
              } else if (
                image?.url
              ) {
                images.push(
                  image.url
                );
              } else if (
                image?.image_path
              ) {
                images.push(
                  image.image_path
                );
              } else if (
                image?.image
              ) {
                images.push(
                  image.image
                );
              }
            }
          );
        }

        return [
          ...new Set(
            images.filter(Boolean)
          ),
        ];
      },
      []
    );

  const productImages =
    getProductImages(product);

  // =====================================================
  // IMAGE
  // =====================================================

  const nextImage = () => {
    if (
      productImages.length <= 1
    ) {
      return;
    }

    setCurrentImage(
      (prev) =>
        prev + 1 >=
        productImages.length
          ? 0
          : prev + 1
    );
  };

  const previousImage = () => {
    if (
      productImages.length <= 1
    ) {
      return;
    }

    setCurrentImage(
      (prev) =>
        prev - 1 < 0
          ? productImages.length - 1
          : prev - 1
    );
  };

  // =====================================================
  // SCAN AGAIN
  // =====================================================

  const scanAgain = () => {
    if (
      restartTimerRef.current
    ) {
      clearTimeout(
        restartTimerRef.current
      );
    }

    scannedRef.current = false;

    setError("");
    setProduct(null);
    setProductError("");
    setProductLoading(false);
    setCurrentImage(0);
    setShowProductModal(false);

    stopScanner();

    restartTimerRef.current =
      setTimeout(() => {
        if (
          mountedRef.current
        ) {
          startScannerRef.current?.();
        }
      }, 200);
  };

  // =====================================================
  // CLOSE PRODUCT
  // =====================================================

  const closeProductModal = () => {
    setShowProductModal(false);

    setProduct(null);
    setProductError("");
    setProductLoading(false);
    setCurrentImage(0);

    scannedRef.current = false;

    stopScanner();

    restartTimerRef.current =
      setTimeout(() => {
        if (
          mountedRef.current
        ) {
          startScannerRef.current?.();
        }
      }, 200);
  };

  // =====================================================
  // CLOSE PAGE
  // =====================================================

  const closeScannerPage = () => {
    if (
      restartTimerRef.current
    ) {
      clearTimeout(
        restartTimerRef.current
      );

      restartTimerRef.current =
        null;
    }

    scannedRef.current = true;

    stopScanner();

    setShowProductModal(false);
    setShowCustomerModal(false);

    setProduct(null);

    navigate(-1);
  };

  // =====================================================
  // PRODUCT NAME
  // =====================================================

  const productName =
    product?.name ||
    product?.product_name ||
    product?.title ||
    "Product";

  const qualityName =
    product?.quality_name ||
    productName ||
    "Product";

  const designShade =
    product?.designshade || "";


  // =====================================================
  // RETURN
  // =====================================================

  return {
    videoRef,
    selectedCustomer,
    showCustomerModal,
    setShowCustomerModal,
    customerSearch,
    setCustomerSearch,
    customers,
    customerLoading,
    showCustomerDropdown,
    setShowCustomerDropdown,
    activeCustomerIndex,
    setActiveCustomerIndex,
    scanning,
    starting,
    error,
    product,
    productLoading,
    productError,
    showProductModal,
    setShowProductModal,
    currentImage,
    productImages,
    nextImage,
    previousImage,
    productName,
    qualityName,
    designShade,
    closeCustomerModal,
    closeProductModal,
    closeScannerPage,
    scanAgain,
    handleCustomerSelect,
    handleCustomerKeyDown,

    highlightText,
  };
};

export default useQRScanner;
